import { memo, useCallback, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { InputHelper } from '@divlab/divanui';

import * as ApiPecom from '@Api/Pecom';
import useTranslation from '@Queries/useTranslation';
import { useRequest } from '@Contexts/Request';
import { useDeps } from '@Contexts/DI';

import type { ChangeEvent, FC } from 'react';
import type { DaDataHint } from '@Types/DaData';
import type { InputHelperHint, InputHelperProps } from '@divlab/divanui';

export type AddressHint = InputHelperHint<DaDataHint>;

export interface InputHelperAddressProps extends Omit<InputHelperProps, 'hints' | 'notFoundText'> {
  onChangeHints?: (hints: AddressHint[]) => void;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
}

const InputHelperAddress: FC<InputHelperAddressProps> = (props) => {
  const { onSelectHint, onChangeHints, onChange, ...restProps } = props;
  const { t } = useTranslation();
  const { logger } = useDeps();
  const { country, language } = useRequest();
  const [hints, setHints] = useState<AddressHint[]>([]);
  const abortController = useRef<AbortController>(null);

  const debouceChangeValue = useDebouncedCallback(async (chunk: string) => {
    if (!chunk || chunk.length < 3) return;

    try {
      // Отменяем предыдущий запрос
      if (abortController.current) {
        abortController.current.abort('abort previous request');
      }

      abortController.current = new AbortController();

      const daDataHints = await ApiPecom.getDaDataHints(
        { chunk, country, currentLang: language.id },
        { signal: abortController.current.signal },
      );

      const addressHints = daDataHints.map((addressHint, index) => ({
        id: index,
        title: addressHint.fullAddress,
        data: addressHint,
      }));

      setHints(addressHints);
      if (onChangeHints) onChangeHints(addressHints);
    } catch (err) {
      // Запрос был прерван нами, ошибкой не считаем
      if (err.name === 'AbortError') return;

      logger.log(err);
    }
  }, 400);

  const handleChangeAddress = useCallback(
    (e) => {
      if (onChange) onChange(e);
      debouceChangeValue(e.target.value);
    },
    [debouceChangeValue, onChange],
  );

  return (
    <InputHelper
      {...restProps}
      hints={hints}
      autoComplete='off'
      notFoundText={t('ui.notfound')}
      onChange={handleChangeAddress}
      onSelectHint={onSelectHint}
    />
  );
};

export default memo(InputHelperAddress);
